export function ready(fn: () => void): void {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

/** Injects markup and returns the nodes added, so they can be removed later. */
export function injectHtml(position: 'head' | 'body_start' | 'body_end', html: string): Node[] {
  const template = document.createElement('template');
  template.innerHTML = html.trim();

  const nodes = Array.from(template.content.childNodes);
  const created: Node[] = [];

  for (const node of nodes) {
    if (node.nodeName === 'SCRIPT') {
      const old = node as HTMLScriptElement;
      const script = document.createElement('script');
      for (const attr of Array.from(old.attributes)) {
        script.setAttribute(attr.name, attr.value);
      }
      script.text = old.textContent || '';
      place(position, script);
      created.push(script);
    } else {
      place(position, node);
      created.push(node);
    }
  }

  return created;
}

function place(position: 'head' | 'body_start' | 'body_end', node: Node): void {
  if (position === 'head') {
    document.head.appendChild(node);
    return;
  }

  if (position === 'body_start') {
    document.body.insertBefore(node, document.body.firstChild);
    return;
  }

  document.body.appendChild(node);
}

export function loadExternalScript(src: string, attrs: Record<string, string> = {}): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    Object.entries(attrs).forEach(([key, value]) => script.setAttribute(key, value));
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function runtimeScript(): HTMLScriptElement | null {
  const current = document.currentScript as HTMLScriptElement | null;
  if (current?.dataset.site || current?.dataset.previewToken) {
    return current;
  }

  return document.querySelector(
    'script[data-site], script[data-preview-token]',
  ) as HTMLScriptElement | null;
}

function tagDataset(): DOMStringMap | null {
  return runtimeScript()?.dataset ?? null;
}

export function getSiteKey(): string | null {
  return tagDataset()?.site ?? null;
}

/**
 * API base for config/consent/beacon.
 * Prefer data-api; otherwise use the origin that served runtime.js + /api
 * (so https://orbit.me-ai.ae/runtime.js → https://orbit.me-ai.ae/api).
 */
export function getApiBase(): string {
  const script = runtimeScript();
  const explicit = script?.dataset.api?.replace(/\/$/, '');
  if (explicit) return explicit;

  if (script?.src) {
    try {
      return `${new URL(script.src).origin}/api`;
    } catch {
      /* fall through */
    }
  }

  return 'http://localhost:8000/api';
}

/** Present on the playground sandbox: load a draft snapshot instead of the live config. */
export function getPreviewToken(): string | null {
  return tagDataset()?.previewToken ?? null;
}

export function isDebugRequested(): boolean {
  const value = tagDataset()?.debug;

  return value === '1' || value === 'true';
}
